// Time and timer utilities for game loop and countdown management

let gameLoopInterval = null;
let activeTimers = new Map();

export function startGameLoop() {
  // Start the main game loop (1 second interval)
  if (gameLoopInterval) {
    clearInterval(gameLoopInterval);
  }
  
  gameLoopInterval = setInterval(() => {
    updateActiveTimers();
    broadcastTimeUpdate();
  }, 1000);
  
  console.log('Game loop started');
}

export function stopGameLoop() {
  // Stop the main game loop
  if (gameLoopInterval) {
    clearInterval(gameLoopInterval);
    gameLoopInterval = null;
  }
  
  console.log('Game loop stopped');
}

export function updateActiveTimers() {
  // Update all active timers
  const now = Date.now();
  const store = window.Alpine?.store('app');
  
  if (!store) return;
  
  // Check each timer type
  Object.keys(store.timers).forEach(timerType => {
    const timer = store.timers[timerType];
    
    if (timer && timer.endTime) {
      const timeLeft = timer.endTime - now;
      
      if (timeLeft <= 0) {
        // Timer finished
        handleTimerComplete(timerType, timer);
        store.clearTimer(timerType);
      } else {
        // Timer still running, check for blink threshold
        checkBlinkThreshold(timerType, timeLeft);
      }
    }
  });
}

function handleTimerComplete(timerType, timer) {
  // Handle timer completion
  console.log(`Timer completed: ${timerType}`);
  
  // Execute callback if provided
  if (timer.callback && typeof timer.callback === 'function') {
    timer.callback();
  }
  
  // Trigger completion event
  window.dispatchEvent(new CustomEvent('timer-complete', {
    detail: { timerType, timer, timestamp: Date.now() }
  }));
  
  // Add completion notification
  const store = window.Alpine?.store('app');
  if (store) {
    const messages = {
      mission: '¡Misión completada!',
      travel: '¡Llegaste a tu destino!',
      crafting: '¡Creación terminada!',
      rest: '¡Te sientes descansado!'
    };
    
    store.addNotification(messages[timerType] || '¡Actividad completada!', 'success');
  }
}

function checkBlinkThreshold(timerType, timeLeft) {
  // Make timers blink when less than 10 seconds remain
  const element = document.querySelector(`[data-timer="${timerType}"]`);
  
  if (element && timeLeft < 10000) { // 10 seconds
    element.classList.add('pulse');
  } else if (element) {
    element.classList.remove('pulse');
  }
}

export function broadcastTimeUpdate() {
  // Broadcast time update event for components to listen to
  window.dispatchEvent(new CustomEvent('time-update', {
    detail: { timestamp: Date.now() }
  }));
}

export function formatTime(seconds) {
  // Format seconds into readable time string
  if (seconds < 0) return '00:00';
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  
  if (hours > 0) {
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  } else {
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
}

export function formatTimeAgo(timestamp) {
  // Format time ago string (e.g., "5 minutes ago")
  const now = Date.now();
  const diff = now - timestamp;
  
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  
  if (days > 0) {
    return `hace ${days} día${days > 1 ? 's' : ''}`;
  } else if (hours > 0) {
    return `hace ${hours} hora${hours > 1 ? 's' : ''}`;
  } else if (minutes > 0) {
    return `hace ${minutes} minuto${minutes > 1 ? 's' : ''}`;
  } else {
    return 'hace un momento';
  }
}

export function formatETA(endTime) {
  // Format estimated time of arrival
  const now = Date.now();
  const timeLeft = endTime - now;
  
  if (timeLeft <= 0) {
    return 'Completado';
  }
  
  const totalSeconds = Math.ceil(timeLeft / 1000);
  return formatTime(totalSeconds);
}

export function getTimeLeft(endTime) {
  // Get time left in milliseconds
  const now = Date.now();
  return Math.max(0, endTime - now);
}

export function getTimeLeftSeconds(endTime) {
  // Get time left in seconds
  return Math.ceil(getTimeLeft(endTime) / 1000);
}

export function createTimer(duration, callback) {
  // Create a new timer
  const endTime = Date.now() + (duration * 1000);
  
  return {
    endTime,
    callback,
    startTime: Date.now(),
    duration: duration * 1000,
    get timeLeft() {
      return getTimeLeft(this.endTime);
    },
    get timeLeftSeconds() {
      return getTimeLeftSeconds(this.endTime);
    },
    get progress() {
      const elapsed = Date.now() - this.startTime;
      return Math.min(1, elapsed / this.duration);
    },
    get eta() {
      return formatETA(this.endTime);
    }
  };
}

export function addTimer(id, duration, callback) {
  // Add a timer to the active timers map
  const timer = createTimer(duration, callback);
  activeTimers.set(id, timer);
  return timer;
}

export function removeTimer(id) {
  // Remove a timer from active timers
  return activeTimers.delete(id);
}

export function getTimer(id) {
  // Get a timer by ID
  return activeTimers.get(id);
}

export function getAllTimers() {
  // Get all active timers
  return Array.from(activeTimers.entries()).map(([id, timer]) => ({
    id,
    ...timer
  }));
}

// Time-based game mechanics
export function calculateTimeDegradation(lastUpdate, currentTime, rate) {
  // Calculate how much a stat should degrade based on time passed
  const timeDiff = currentTime - lastUpdate;
  const minutesPassed = timeDiff / (1000 * 60);
  return rate * minutesPassed;
}

export function getGameTime() {
  // Get current game time (can be different from real time for game mechanics)
  const store = window.Alpine?.store('app');
  if (!store) return Date.now();
  
  // For now, use real time, but this could implement time scaling
  return Date.now();
}

export function formatGameTime(timestamp = null) {
  // Format game time for display
  const time = timestamp || getGameTime();
  return new Date(time).toLocaleTimeString();
}

export function formatGameDate(timestamp = null) {
  // Format game date for display
  const time = timestamp || getGameTime();
  return new Date(time).toLocaleDateString();
}

// Animation timing utilities for anime.js integration
export function getAnimationDuration(baseMs, speedMultiplier = 1) {
  // Calculate animation duration with speed multiplier
  return Math.max(100, baseMs / speedMultiplier); // Minimum 100ms
}

export function createPulseAnimation(element) {
  // Create pulse animation for urgent timers
  if (element) {
    element.style.animation = 'pulse 1s ease-in-out infinite';
  }
}

export function removePulseAnimation(element) {
  // Remove pulse animation
  if (element) {
    element.style.animation = '';
  }
}

// Cleanup function
export function cleanup() {
  // Clean up all timers and intervals
  stopGameLoop();
  activeTimers.clear();
  console.log('Time utilities cleaned up');
}